import {
  Building,
  calendarDateFromTimestamp,
  classifyManagementFiltersEntitiesIds,
  Contact,
  contactMostImportantType,
  ContactType,
  DocumentCategoryWithoutReadIdAndClientId,
  DocumentSortOptions,
  EntityType,
  ExtendedSearchModel,
  FileCategory,
  filterAndMap,
  FolderEntity,
  FolderSortInvoiceOptions,
  getMainUnit,
  getRegion,
  getSignatureDisplayStatus,
  getTranslatedCategory,
  getTypedLanguage,
  isAfter,
  isBefore,
  isLeaseStartDateBeforeReductionPeriod,
  Language,
  LeaseExtended,
  MimeTypes,
  SignatureDisplayStatus,
  SignatureDocumentStatus,
  FilterGroup,
  toCalendarDate,
  Unit,
} from '@rentguru/commons-utils';
import { groupBy, isNil, orderBy, uniqBy } from 'lodash';
import { IntlFormatters } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { BuildingContext } from 'src/hooks/BuildingsContext';
import { ConfirmationDialogContext, ConfirmationDialogOptions } from 'src/hooks/ConfirmationDialogContext';
import { FilesContext } from 'src/hooks/FilesContext';
import { type FolderDatabaseModel } from 'src/hooks/Folderhook/folderContextUtils';
import { LeaseContext } from 'src/hooks/LeasesContext';
import { SignatureDocumentsContext } from 'src/hooks/SignatureDocumentContext';
import { TechnicsContext } from 'src/hooks/TechnicsContext';

import { getTranslatedLabel } from './folderWithDBUtils';

export const DELAY_RENDER = 30; // delay to show the data
export const DELAY_IN_BETWEEN_SYNC = 2000; // delay in between syncs

export type FolderDragData = {
  folder: FolderDatabaseModel;
};

export enum FolderItemDialogTypes {
  ADD_FILE,
  ADD_FOLDER,
  MOVE_FOLDER,
  UPLOAD_PROGRESSION,
  NONE,
}

export const useQuery = () => {
  const location = useLocation();
  return new URLSearchParams(location?.search || '');
};

export const makeSortedList = (folders: FolderDatabaseModel[]) => {
  const groupedFolders = groupBy(folders, (folder) => folder.folderEntity);

  const groupedFolderEntityFolders = groupBy(
    groupedFolders[FolderEntity.FOLDER],
    (folder) => folder.categoryType === FileCategory.CUSTOM
  );

  const sortedList = [
    ...(groupedFolders[FolderEntity.CONTACT] ?? []),
    ...(groupedFolders[FolderEntity.JOINT_CONTACT] ?? []),
    ...(groupedFolders[FolderEntity.BUILDING] ?? []),
    ...(groupedFolders[FolderEntity.UNIT] ?? []),
    ...(groupedFolders[FolderEntity.LEASE] ?? []),
    ...(groupedFolderEntityFolders['false'] ?? []),
    ...(groupedFolderEntityFolders['true'] ?? []),
    ...(groupedFolders[FolderEntity.FILE] ?? []),
  ];

  return sortedList;
};

export const sortFolders = (
  folders: FolderDatabaseModel[],
  sortOption: DocumentSortOptions,
  language: Language
): FolderDatabaseModel[] => {
  switch (sortOption) {
    case DocumentSortOptions.ALPHANUMERIC_ASC: {
      const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

      const alphanumeric = folders.sort((a, b) => {
        const aLabel = a.contactLabelToSortOn ?? getTranslatedLabel(a, language);
        const bLabel = b.contactLabelToSortOn ?? getTranslatedLabel(b, language);
        return collator.compare(aLabel!, bLabel!);
      });
      return makeSortedList(alphanumeric);
    }
    case DocumentSortOptions.ALPHANUMERIC_DES: {
      const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

      const alphanumeric = folders.sort((a, b) => {
        const aLabel = a.contactLabelToSortOn ?? getTranslatedLabel(a, language);
        const bLabel = b.contactLabelToSortOn ?? getTranslatedLabel(b, language);
        return collator.compare(aLabel!, bLabel!);
      });
      return makeSortedList(alphanumeric.reverse());
    }
    case DocumentSortOptions.SIZE_ASC: {
      const orderedByFile = orderBy(folders, [(folder) => folder.size], ['asc']);

      const newSortedFolders = makeSortedList(orderedByFile);

      return newSortedFolders;
    }
    case DocumentSortOptions.SIZE_DES: {
      const orderedByFile = orderBy(folders, [(folder) => folder.size], ['desc']);

      const newSortedFolders = makeSortedList(orderedByFile);

      return newSortedFolders;
    }
    case DocumentSortOptions.DATE_ASC: {
      const orderByDate = orderBy(folders, [(folder) => folder.updatedAt ?? ''], ['asc']);

      const newSortedFolders = makeSortedList(orderByDate);

      return newSortedFolders;
    }
    case DocumentSortOptions.DATE_DES: {
      const orderByDate = orderBy(folders, [(folder) => folder.updatedAt ?? ''], ['desc']);

      const newSortedFolders = makeSortedList(orderByDate);

      return newSortedFolders;
    }
    default:
      return makeSortedList(folders);
  }
};

export const folderFilterForSearch =
  (
    extendedSearchModel: ExtendedSearchModel,
    searchText: string,
    language: Language,
    onPage: boolean,
    lastFolderEntityId?: string
  ) =>
  (folder: FolderDatabaseModel): boolean => {
    if (
      !onPage &&
      (folder.entityId !== lastFolderEntityId ||
        (folder.entityId === lastFolderEntityId &&
          ![FolderEntity.FILE, FolderEntity.FOLDER].includes(folder.folderEntity)))
    ) {
      return false;
    }
    // Entity filter
    if (extendedSearchModel.folderEntity && extendedSearchModel.folderEntity !== folder.categoryEntity) {
      return false;
    }

    // Category filter
    if (extendedSearchModel.folderCategory && extendedSearchModel.folderCategory.id !== folder.documentCategoryId) {
      return false;
    }

    // Date range filters
    const folderDate = folder.updatedAt ? calendarDateFromTimestamp(folder.updatedAt) : null;
    if (folderDate && extendedSearchModel.fromDate && isBefore(folderDate, extendedSearchModel.fromDate)) {
      return false;
    }

    if (folderDate && extendedSearchModel.toDate && isAfter(folderDate, extendedSearchModel.toDate)) {
      return false;
    }

    // Text search filter
    const label = getTranslatedLabel(folder, getTypedLanguage(language));
    return label.toLowerCase().includes(searchText.toLowerCase());
  };

export const getFolderSize = (foldersByDepth: FolderDatabaseModel[]): number =>
  foldersByDepth.reduce((acc, folder) => {
    if (isNil(folder.size)) return acc;
    return acc + Number(folder.size);
  }, 0);

export const sortFoldersOnType = (foldersToSort: FolderDatabaseModel[], sortTypes: string[]) =>
  foldersToSort.filter((folder) => {
    if (!folder?.mimeType) return false;
    const typeToSortOn = folder!.mimeType!.includes(MimeTypes.IMAGE) ? MimeTypes.ALL_IMAGES : folder!.mimeType!;

    return sortTypes.includes(typeToSortOn);
  });

export const sortFoldersOnInvoicePaid = (
  foldersToSort: FolderDatabaseModel[],
  invoicePaid: FolderSortInvoiceOptions[]
) =>
  foldersToSort.filter((folder) => {
    if (folder.isInvoicePaid == null) return false;
    const folderInvoiceStatus = folder.isInvoicePaid ? FolderSortInvoiceOptions.PAID : FolderSortInvoiceOptions.UNPAID;
    return invoicePaid.includes(folderInvoiceStatus);
  });

export const getSignatureDisplayStatusOfFolder = (
  folder: FolderDatabaseModel,
  getSignatureDocumentForFile: SignatureDocumentsContext['getSignatureDocumentForFile']
): SignatureDisplayStatus | undefined => {
  if (isNil(folder.documentId)) {
    return undefined;
  }
  const signatureDocument = getSignatureDocumentForFile(folder.documentId);
  if (isNil(signatureDocument)) {
    return undefined;
  }
  return getSignatureDisplayStatus(signatureDocument.status, signatureDocument.signatureResults);
};

export const filterFoldersOnSignatureStatus = (
  foldersToFilter: FolderDatabaseModel[],
  signatureStatuses: SignatureDisplayStatus[],
  getSignatureStatusOfFolder: (folder: FolderDatabaseModel) => SignatureDisplayStatus | undefined
) =>
  foldersToFilter.filter((folder) => {
    const signatureStatus = getSignatureStatusOfFolder(folder);
    if (isNil(signatureStatus)) {
      return false;
    }
    return signatureStatuses.includes(signatureStatus);
  });

export const getConfirmationDialogFields = (
  signatureDocumentStatus: SignatureDocumentStatus,
  formatMessage: IntlFormatters['formatMessage']
) => {
  switch (signatureDocumentStatus) {
    case SignatureDocumentStatus.SIGNED:
      return {
        title: formatMessage({ id: 'folders.fileActions.deleteSignedDocument' }),
        text: formatMessage({ id: 'folders.fileActions.deleteSignedDocumentWarning' }),
      };
    case SignatureDocumentStatus.PENDING:
      return {
        title: formatMessage({ id: 'folders.fileActions.deletePendingDocument' }),
        text: formatMessage({ id: 'folders.fileActions.deletePendingDocumentWarning' }),
      };
    case SignatureDocumentStatus.REJECTED:
      return {
        title: formatMessage({ id: 'folders.fileActions.deleteRejectedDocument' }),
        text: formatMessage({ id: 'folders.fileActions.deleteRejectedDocumentWarning' }),
      };
    default:
      return {
        title: formatMessage({ id: 'folders.fileActions.deleteDocumentWithSignature' }),
        text: formatMessage({ id: 'folders.fileActions.deleteDocumentWarningWithSignature' }),
      };
  }
};

export const sortDocumentCategories = (
  categories: DocumentCategoryWithoutReadIdAndClientId[],
  language: string | null
) => {
  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

  const alphanumeric = categories.sort((a, b) =>
    collator.compare(getTranslatedCategory(a, language), getTranslatedCategory(b, language))
  );

  const groupedList = groupBy(alphanumeric, (category) => category.fileCategory === FileCategory.CUSTOM);
  const sortedList = [...(groupedList['false'] ?? []), ...(groupedList['true'] ?? [])];

  return sortedList;
};
export enum DeleteStatus {
  DELETED,
  CANCELLED,
}

export const checkAndDeleteSignature = async (
  documentId: string,
  getSignatureDocumentForFile: SignatureDocumentsContext['getSignatureDocumentForFile'],
  getConfirmation: ConfirmationDialogContext['getConfirmation'],
  setSignatureResultToZero: SignatureDocumentsContext['setSignatureResultToZero'],
  deleteSignatureDocument: SignatureDocumentsContext['deleteSignatureDocument'],
  formatMessage: IntlFormatters['formatMessage']
) => {
  const fileWithSignatureResult = getSignatureDocumentForFile(documentId);
  if (fileWithSignatureResult) {
    const { title, text } = getConfirmationDialogFields(
      fileWithSignatureResult?.status ?? SignatureDocumentStatus.DRAFT,
      formatMessage
    );
    const confirmation = await getConfirmation({
      title,
      text,
      actionButtonLabel: formatMessage({ id: 'delete' }),
      formatMessage,
    });
    if (confirmation !== ConfirmationDialogOptions.ACTION_BUTTON) {
      return DeleteStatus.CANCELLED;
    }

    if (fileWithSignatureResult?.status === SignatureDocumentStatus.PENDING) {
      await setSignatureResultToZero(fileWithSignatureResult);
    }
    await deleteSignatureDocument(fileWithSignatureResult!);

    return DeleteStatus.DELETED;
  }
};

export enum EpbDialog {
  EPB_DELETE_CONFIRMATION,
  EPB_PROPERTY_DELETE_CONFIRMATION,
}

export const checkAndDeleteEPB = async (
  documentId: string,
  leases: LeaseExtended[],
  buildings: Building[],
  currentEntityId: string,
  getTechnicsFor: TechnicsContext['getTechnicsFor'],
  fetchFileFromId: FilesContext['fetchFileFromId'],
  setCurrentEntityName: (name: string) => void,
  setIsEPB: (isEPB: { isEpb: boolean; beforePeriod: boolean; currentTechnic: string | undefined }) => void,
  setConfirmDialog: (dialog: EpbDialog) => void
) => {
  const currentLease = leases.find((lease) => lease.id === currentEntityId);
  const currentTechnics = getTechnicsFor(currentEntityId);

  const file = await fetchFileFromId(documentId);
  const currentTechnic = currentTechnics.find((technic) => technic.id === file[0].foreignKey);
  if (isNil(currentTechnic)) {
    return false;
  }

  const currentEntity = currentTechnic.unit ?? currentTechnic.building;

  setCurrentEntityName(currentEntity!.name);

  if (currentLease?.startDate) {
    const region = getRegion(currentLease, buildings);
    const isLeaseBeforeReductionPeriod = isLeaseStartDateBeforeReductionPeriod(
      toCalendarDate(currentLease.startDate),
      region
    );

    setIsEPB({
      isEpb: true,
      beforePeriod: isLeaseBeforeReductionPeriod ?? false,
      currentTechnic: currentTechnic?.id ?? undefined,
    });
    setConfirmDialog(EpbDialog.EPB_DELETE_CONFIRMATION);
    return false;
  } else {
    setIsEPB({ isEpb: true, beforePeriod: false, currentTechnic: currentTechnic?.id ?? '' });
    setConfirmDialog(EpbDialog.EPB_PROPERTY_DELETE_CONFIRMATION);
    return false;
  }
};

export const calculateContactsToUseInDialog = (
  contacts: Contact[],
  entity: EntityType,
  entityId: string,
  members: Contact[],
  units: Unit[],
  buildings: Building[],
  getLease: LeaseContext['getLease'],
  getBuilding: BuildingContext['getBuilding']
): { preferredContactsIds: string[]; contactsToUse: Contact[] } => {
  const filteredContacts = contacts.filter((contact) => contactMostImportantType(contact) !== ContactType.CLIENT);

  switch (entity) {
    case EntityType.LEASE:
      const lease = getLease(entityId);
      const leaseTenants = lease?.tenants ?? [];
      const leaseGuarantors = lease?.guarantors ?? [];
      const leaseLessors = lease?.lessors ?? [];
      const mainUnitOfLease = getMainUnit(lease?.units ?? []);
      const buildingIdOfLease = mainUnitOfLease?.buildingId;
      const currentBuildingOfLease = getBuilding(buildingIdOfLease ?? '');
      const buildingOwners =
        currentBuildingOfLease?.owners?.reduce<Contact[]>(
          filterAndMap({
            filter: (buildingOwner) => !isNil(buildingOwner.owner),
            map: (buildingOwner) => buildingOwner.owner!,
          }),
          []
        ) ?? [];
      const allContactsOfTheLease = uniqBy(
        [...leaseTenants, ...leaseLessors, ...leaseGuarantors, ...buildingOwners],
        'id'
      );
      const leaseContacts = uniqBy([...allContactsOfTheLease, ...members], 'id');
      const preferredContactsIds = allContactsOfTheLease.map((contact) => contact.id);
      return { preferredContactsIds, contactsToUse: leaseContacts };
    case EntityType.BUILDING:
      const currentBuilding = getBuilding(entityId);
      const preferredContacts = currentBuilding?.owners?.map((buildingOwner) => buildingOwner.contactId) ?? [];
      return { preferredContactsIds: preferredContacts, contactsToUse: filteredContacts };
    case EntityType.UNIT:
      const currentUnit = units.find((unit) => unit.id === entityId);
      const currentUnitBuilding = buildings.find((building) => building.id === currentUnit?.buildingId);
      const preferredUnitOwners = currentUnit?.owners?.map((unitOwner) => unitOwner.ownerId) ?? [];
      const currentBuildingOwners = currentUnitBuilding?.owners?.map((buildingOwner) => buildingOwner.contactId) ?? [];

      const allOwners = [...preferredUnitOwners, ...currentBuildingOwners];
      return { preferredContactsIds: allOwners, contactsToUse: filteredContacts };
    default:
      return { preferredContactsIds: [], contactsToUse: filteredContacts };
  }
};

export const getPreferredContactIdsAndContactToUse = (
  entity: EntityType,
  entityId: string,
  getLease: LeaseContext['getLease'],
  getBuilding: BuildingContext['getBuilding'],
  filteredContacts: Contact[],
  members: Contact[],
  units: Unit[],
  buildings: Building[]
): { preferredContactsIds: string[]; contactsToShow: Contact[] } => {
  switch (entity) {
    case EntityType.LEASE:
      const lease = getLease(entityId);
      const leaseTenants = lease?.tenants ?? [];
      const leaseGuarantors = lease?.guarantors ?? [];
      const leaseLessors = lease?.lessors ?? [];
      const mainUnitOfLease = getMainUnit(lease?.units ?? []);
      const buildingIdOfLease = mainUnitOfLease?.buildingId;
      const currentBuildingOfLease = getBuilding(buildingIdOfLease ?? '');
      const buildingOwners =
        currentBuildingOfLease?.owners?.reduce<Contact[]>((acc, buildingOwner) => {
          if (buildingOwner.owner) {
            acc.push(buildingOwner.owner);
          }
          return acc;
        }, []) ?? [];
      const allContactsOfTheLease = uniqBy(
        [...leaseTenants, ...leaseLessors, ...leaseGuarantors, ...buildingOwners],
        'id'
      );
      const leaseContacts = uniqBy([...allContactsOfTheLease, ...members], 'id');
      return {
        preferredContactsIds: allContactsOfTheLease.map((contact) => contact.id),
        contactsToShow: leaseContacts,
      };
    case EntityType.BUILDING:
      const currentBuilding = getBuilding(entityId);
      const preferredContacts = currentBuilding?.owners?.map((buildingOwner) => buildingOwner.contactId) ?? [];
      return { preferredContactsIds: preferredContacts, contactsToShow: filteredContacts };
    case EntityType.UNIT:
      const currentUnit = units.find((unit) => unit.id === entityId);
      const currentUnitBuilding = buildings.find((building) => building.id === currentUnit?.buildingId);
      const preferredUnitOwners = currentUnit?.owners?.map((unitOwner) => unitOwner.ownerId) ?? [];
      const currentBuildingOwners = currentUnitBuilding?.owners?.map((buildingOwner) => buildingOwner.contactId) ?? [];
      const allOwners = [...preferredUnitOwners, ...currentBuildingOwners];
      return { preferredContactsIds: allOwners, contactsToShow: filteredContacts };
    default:
      return { preferredContactsIds: [], contactsToShow: filteredContacts };
  }
};

export const filterFoldersForManagementFilters = (folders: FolderDatabaseModel[], managementFilters: FilterGroup[]) => {
  const entitiesToKeep = classifyManagementFiltersEntitiesIds(managementFilters);
  return folders.filter((folder) => {
    switch (folder.categoryEntity) {
      case EntityType.BUILDING:
        return entitiesToKeep.buildingIds.includes(folder.entityId);
      case EntityType.UNIT:
        return entitiesToKeep.unitIds.includes(folder.entityId);
      default:
        return true;
    }
  });
};
